/**
 * WhitelistSyncModal — synchronizacja kont bankowych kontrahenta z białą listą
 * podatników VAT MF.
 *
 * Flow (frozen API contract):
 *  1. Otwarcie modala → `POST .../whitelist-sync/preview` (nic nie zapisuje) —
 *     zwraca status obliczony dla istniejących kont + listę rachunków z białej
 *     listy, których kontrahent jeszcze nie ma zapisanych (`toAdd`).
 *  2. User odznacza/zaznacza `toAdd` (domyślnie WSZYSTKIE zaznaczone) i
 *     zatwierdza → `POST .../whitelist-sync/apply` z `addNrbs` zaznaczonych.
 *  3. Sukces → invalidacja listy kont kontrahenta (ten sam query key co
 *     `BankAccountsDynamicTab`), toast, zamknięcie modala.
 *
 * Błędy:
 *  - `MF_RATE_LIMITED` (429) → osobny komunikat ("limit MF wyczerpany na dziś") —
 *    zarówno gdy padnie na `preview` (inline w treści modala), jak i na `apply`
 *    (toast w `onError`).
 *  - inne → generic komunikat błędu (loadError / genericError).
 *
 * `meta.rateLimited` (z preview) — limit MF trafiony PODCZAS liczenia
 * podglądu (część kont wirtualnych nie została zweryfikowana) — inny scenariusz
 * niż twardy błąd 429 (request cały padł). Oba pokazujemy userowi, każdy inaczej.
 *
 * `meta.virtualDeferred` (z preview) — podgląd CELOWO nie weryfikuje kont
 * wirtualnych (pokazane jako `virtual_unverified`) — zostaną sprawdzone dopiero
 * przy `apply`. Osobna neutralna notka informacyjna (nie ostrzeżenie).
 *
 * `meta.rateLimited` (z apply, response sukcesu) — zapis mimo to się powiódł,
 * ale część kont wirtualnych nie została zweryfikowana podczas zapisu — osobny
 * toast ostrzegawczy PO toaście sukcesu (żeby nie ukryć degradacji).
 */

import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { Button } from '@/components/Button';
import { Checkbox } from '@/components/Checkbox';
import { Modal } from '@/components/Modal';
import { Skeleton } from '@/components/Skeleton';
import { useToast } from '@/contexts/ToastContext';
import { api } from '@/lib/api';
import { formatIban } from '@/lib/iban';

import { type WhitelistStatus, WhitelistStatusBadge } from './WhitelistStatusBadge';

export interface WhitelistSyncModalProps {
  open: boolean;
  onClose: () => void;
  contractorId: string;
}

interface PreviewExistingRow {
  accountId: string;
  accountNumber: string;
  computedStatus: WhitelistStatus;
}

interface PreviewToAddRow {
  nrb: string;
  iban: string;
}

interface PreviewMeta {
  hasVirtualAccounts: boolean;
  source: 'mf' | 'cache';
  fetchedAt: string;
  rateLimited: boolean;
  /** `true` gdy podgląd CELOWO nie zweryfikował kont wirtualnych (pokazane jako
   *  `virtual_unverified`) — zostaną sprawdzone dopiero przy `apply`. Inny scenariusz
   *  niż `rateLimited` (limit MF trafiony PODCZAS liczenia podglądu). */
  virtualDeferred: boolean;
}

interface PreviewResponse {
  existing: PreviewExistingRow[];
  toAdd: PreviewToAddRow[];
  meta: PreviewMeta;
}

interface ApplyResponse {
  added: number;
  statusesSet: number;
  existing: { accountId: string; computedStatus: WhitelistStatus }[];
  meta: { rateLimited: boolean };
}

interface ApiError {
  code: string | null;
}

function extractApiError(err: unknown): ApiError {
  if (!err || typeof err !== 'object') return { code: null };
  const maybe = err as { response?: { data?: { error?: string } } };
  return { code: maybe.response?.data?.error ?? null };
}

/** `pl-PL` — konwencja formatowania dat w tej SPA (patrz np. `AdminUserDrawer.tsx`). */
function formatFetchedAtDate(iso: string): string {
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? iso : d.toLocaleDateString('pl-PL');
}

export function WhitelistSyncModal({ open, onClose, contractorId }: WhitelistSyncModalProps) {
  const { t } = useTranslation();
  const toast = useToast();
  const queryClient = useQueryClient();

  // nrb → zaznaczone do dodania. Seedowane domyślnie-zaznaczone przy każdym
  // świeżym `previewQ.data` (w tym re-fetch po ponownym otwarciu modala).
  const [checkedNrbs, setCheckedNrbs] = useState<Record<string, boolean>>({});

  const previewQ = useQuery({
    queryKey: ['contractor-bank-accounts-whitelist-preview', contractorId],
    queryFn: async () => {
      const res = await api.post<PreviewResponse>(
        `/contractors/${contractorId}/bank-accounts/whitelist-sync/preview`,
        {},
      );
      return res.data;
    },
    enabled: open,
    staleTime: 0,
    gcTime: 0,
  });

  useEffect(() => {
    if (!previewQ.data) return;
    const initial: Record<string, boolean> = {};
    for (const row of previewQ.data.toAdd) initial[row.nrb] = true;
    setCheckedNrbs(initial);
  }, [previewQ.data]);

  const applyMutation = useMutation({
    mutationFn: async (addNrbs: string[]) => {
      const res = await api.post<ApplyResponse>(
        `/contractors/${contractorId}/bank-accounts/whitelist-sync/apply`,
        { addNrbs },
      );
      return res.data;
    },
    onSuccess: (response) => {
      queryClient.invalidateQueries({ queryKey: ['contractor-bank-accounts', contractorId] });
      toast.show({
        variant: 'success',
        title: t('tenant.contractors.bankAccounts.whitelistSyncModal.toast.success'),
      });
      // Apply zapisuje mimo częściowej degradacji (limit MF trafiony PODCZAS
      // weryfikacji kont wirtualnych w trakcie zapisu) — osobny toast, żeby nie
      // ukryć że część statusów mogła zostać niesprawdzona.
      if (response.meta?.rateLimited === true) {
        toast.show({
          variant: 'warning',
          title: t('tenant.contractors.bankAccounts.whitelistSyncModal.toast.partialRateLimited'),
        });
      }
      onClose();
    },
    onError: (err: unknown) => {
      const apiErr = extractApiError(err);
      if (apiErr.code === 'MF_RATE_LIMITED') {
        toast.show({
          variant: 'error',
          title: t('tenant.contractors.bankAccounts.whitelistSyncModal.toast.mfRateLimited'),
        });
        return;
      }
      toast.show({
        variant: 'error',
        title: t('tenant.contractors.bankAccounts.whitelistSyncModal.toast.genericError'),
      });
    },
  });

  function handleConfirm() {
    const addNrbs = Object.entries(checkedNrbs)
      .filter(([, checked]) => checked)
      .map(([nrb]) => nrb);
    applyMutation.mutate(addNrbs);
  }

  const busy = applyMutation.isPending;

  return (
    <Modal
      open={open}
      onClose={() => (busy ? undefined : onClose())}
      title={t('tenant.contractors.bankAccounts.whitelistSyncModal.title')}
      closeOnBackdrop={!busy}
      closeOnEsc={!busy}
      size="lg"
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={busy}>
            {t('common.cancel')}
          </Button>
          <Button
            variant="primary"
            loading={busy}
            disabled={busy || previewQ.isLoading || previewQ.isError}
            onClick={handleConfirm}
            data-testid="whitelist-sync-confirm-btn"
          >
            {t('tenant.contractors.bankAccounts.whitelistSyncModal.confirm')}
          </Button>
        </>
      }
    >
      {previewQ.isLoading ? (
        <div className="space-y-2" data-testid="whitelist-sync-loading">
          <Skeleton className="h-6" />
          <Skeleton className="h-6" />
          <Skeleton className="h-6" />
        </div>
      ) : previewQ.isError ? (
        <p className="text-sm text-red-600" data-testid="whitelist-sync-load-error">
          {extractApiError(previewQ.error).code === 'MF_RATE_LIMITED'
            ? t('tenant.contractors.bankAccounts.whitelistSyncModal.toast.mfRateLimited')
            : t('tenant.contractors.bankAccounts.whitelistSyncModal.loadError')}
        </p>
      ) : previewQ.data ? (
        <div className="space-y-4" data-testid="whitelist-sync-content">
          {previewQ.data.meta.rateLimited && (
            <div
              role="alert"
              className="rounded-lg border border-amber-300 bg-amber-50 px-3 py-2 text-xs text-amber-800 dark:border-amber-800 dark:bg-amber-900/20 dark:text-amber-300"
            >
              {t('tenant.contractors.bankAccounts.whitelistSyncModal.rateLimitedNote')}
            </div>
          )}
          {previewQ.data.meta.virtualDeferred && (
            <div className="rounded-lg border border-blue-200 bg-blue-50 px-3 py-2 text-xs text-blue-700 dark:border-blue-800 dark:bg-blue-900/20 dark:text-blue-300">
              {t('tenant.contractors.bankAccounts.whitelistSyncModal.virtualDeferredNote')}
            </div>
          )}
          {previewQ.data.meta.source === 'cache' && (
            <div className="rounded-lg border border-border-secondary bg-bg-secondary px-3 py-2 text-xs text-text-tertiary">
              {t('tenant.contractors.bankAccounts.whitelistSyncModal.cacheNote', {
                date: formatFetchedAtDate(previewQ.data.meta.fetchedAt),
              })}
            </div>
          )}

          <section>
            <h4 className="mb-2 text-xs font-semibold uppercase text-text-tertiary">
              {t('tenant.contractors.bankAccounts.whitelistSyncModal.existingSectionTitle')}
            </h4>
            {previewQ.data.existing.length === 0 ? (
              <p className="text-xs text-text-tertiary">
                {t('tenant.contractors.bankAccounts.whitelistSyncModal.existingEmpty')}
              </p>
            ) : (
              <ul className="space-y-1.5">
                {previewQ.data.existing.map((row) => (
                  <li
                    key={row.accountId}
                    className="flex items-center justify-between gap-2 text-sm"
                    data-testid={`whitelist-sync-existing-${row.accountId}`}
                  >
                    <span className="font-mono text-xs">{formatIban(row.accountNumber)}</span>
                    <WhitelistStatusBadge status={row.computedStatus} />
                  </li>
                ))}
              </ul>
            )}
          </section>

          <section>
            <h4 className="mb-2 text-xs font-semibold uppercase text-text-tertiary">
              {t('tenant.contractors.bankAccounts.whitelistSyncModal.toAddSectionTitle')}
            </h4>
            {previewQ.data.toAdd.length === 0 ? (
              <p className="text-xs text-text-tertiary">
                {t('tenant.contractors.bankAccounts.whitelistSyncModal.toAddEmpty')}
              </p>
            ) : (
              <ul className="space-y-1.5">
                {previewQ.data.toAdd.map((row) => (
                  <li key={row.nrb} data-testid={`whitelist-sync-toadd-${row.nrb}`}>
                    <Checkbox
                      label={<span className="font-mono text-xs">{formatIban(row.iban)}</span>}
                      checked={checkedNrbs[row.nrb] ?? true}
                      onChange={(e) =>
                        setCheckedNrbs((prev) => ({ ...prev, [row.nrb]: e.target.checked }))
                      }
                    />
                  </li>
                ))}
              </ul>
            )}
          </section>
        </div>
      ) : null}
    </Modal>
  );
}
